import { useEffect, useState } from "react";
import AdminLayout from "../AdminLayout";
import SiteTextsPanel from "../SiteTextsPanel";
import { API_BASE } from "@/config/api";
import { mergeFaqConfig } from "@/lib/faqConfig";
import type { Faq } from "@/types/content";

type Draft = { id?: number; pregunta: string; respuesta: string; mostrar_en_inicio: boolean };

export default function AdminFaqs() {
  const [items, setItems] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");
  const [primeraAbierta, setPrimeraAbierta] = useState(true);

  async function load() {
    setLoading(true);
    const res = await fetch(`${API_BASE}/bootstrap`);
    const json = await res.json();
    setItems(json.faqs);
    setPrimeraAbierta(mergeFaqConfig(json.contentBlocks?.home?.faq_config?.[0]).primera_abierta);
    setLoading(false);
  }

  async function saveFaqConfig(): Promise<boolean> {
    const res = await fetch(`${API_BASE}/content-blocks/home/faq_config`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: "config", primera_abierta: primeraAbierta }] }),
    });
    return res.ok;
  }

  // Mueve una pregunta y guarda el orden de inmediato; si falla, vuelve a cargar la lista real.
  async function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    setListError("");
    const res = await fetch(`${API_BASE}/faqs/reorder`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: next.map((f) => f.id) }),
    });
    if (!res.ok) {
      const json = await res.json().catch(() => null);
      setListError(json?.error || "No se pudo guardar el orden.");
      await load();
    }
  }

  async function toggleInicio(faq: Faq, value: boolean) {
    setItems((list) => list.map((f) => (f.id === faq.id ? { ...f, mostrar_en_inicio: value } : f)));
    setListError("");
    const res = await fetch(`${API_BASE}/faqs/${faq.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mostrar_en_inicio: value }),
    });
    if (!res.ok) {
      setListError("No se pudo guardar el cambio.");
      await load();
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSave() {
    if (!editing) return;
    if (!editing.pregunta.trim() || !editing.respuesta.trim()) {
      setError("La pregunta y la respuesta no pueden quedar vacías.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(editing.id ? `${API_BASE}/faqs/${editing.id}` : `${API_BASE}/faqs`, {
        method: editing.id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editing),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setError(json?.error || "No se pudo guardar");
        return;
      }
      setEditing(null);
      await load();
    } catch {
      setError("Error de conexión.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("¿Eliminar esta pregunta?")) return;
    await fetch(`${API_BASE}/faqs/${id}`, { method: "DELETE" });
    await load();
  }

  const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-2xl text-foreground-950">Preguntas frecuentes</h1>
        <button
          type="button"
          onClick={() => setEditing({ pregunta: "", respuesta: "", mostrar_en_inicio: false })}
          className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer"
        >
          + Agregar pregunta
        </button>
      </div>

      {!loading && (
        <div className="mt-6 max-w-2xl">
          <SiteTextsPanel
            title="Sección de preguntas frecuentes en el inicio"
            description="El encabezado y el enlace de la sección de la página de inicio. Qué preguntas aparecen allí se elige más abajo, en cada pregunta."
            keys={["home.faq.etiqueta", "home.faq.titulo", "home.faq.ver_todas"]}
            onSaveExtra={saveFaqConfig}
          >
            <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
              <input
                type="checkbox"
                className="accent-primary-500"
                checked={primeraAbierta}
                onChange={(e) => setPrimeraAbierta(e.target.checked)}
              />
              La primera pregunta viene desplegada al abrir la página (inicio y página de preguntas frecuentes)
            </label>
          </SiteTextsPanel>
        </div>
      )}

      <h2 className="mt-8 font-heading font-semibold text-lg text-foreground-900">Preguntas</h2>
      <p className="mt-1 text-xs text-foreground-500">
        Se muestran en este orden. El inicio solo muestra las que tienen marcada la casilla «Mostrar en el inicio»; la
        página de preguntas frecuentes muestra todas.
      </p>
      {!loading && items.length > 0 && !items.some((f) => f.mostrar_en_inicio) && (
        <div className="mt-3 max-w-2xl rounded-md border border-accent-200 bg-accent-100 p-3 text-sm text-accent-800">
          Ninguna pregunta está marcada para el inicio, así que la sección no se muestra en la página de inicio.
        </div>
      )}
      {listError && (
        <div className="mt-3 max-w-2xl rounded-md border border-accent-200 bg-accent-100 p-3 text-sm text-accent-800">{listError}</div>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-foreground-500">Cargando...</p>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((f, i) => (
            <div key={f.id} className="rounded-lg border border-background-200 bg-background-50 p-4" data-faq-id={f.id}>
              <div className="flex items-start justify-between gap-4">
                <p className="text-sm font-semibold text-foreground-900">
                  <span className="mr-2 text-xs font-normal text-foreground-400">{i + 1}.</span>
                  {f.pregunta}
                </p>
                <div className="flex gap-3 shrink-0">
                  <button
                    type="button"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer disabled:opacity-30"
                  >
                    ↑ Subir
                  </button>
                  <button
                    type="button"
                    disabled={i === items.length - 1}
                    onClick={() => move(i, 1)}
                    className="text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer disabled:opacity-30"
                  >
                    ↓ Bajar
                  </button>
                  <button type="button" onClick={() => setEditing(f)} className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                    Editar
                  </button>
                  <button type="button" onClick={() => handleDelete(f.id)} className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer">
                    Eliminar
                  </button>
                </div>
              </div>
              <p className="mt-1 text-xs text-foreground-500">{f.respuesta}</p>
              <label className="mt-3 inline-flex items-center gap-2 text-xs text-foreground-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-primary-500"
                  checked={f.mostrar_en_inicio}
                  onChange={(e) => toggleInicio(f, e.target.checked)}
                />
                Mostrar en el inicio
              </label>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
          <div className="w-full max-w-lg rounded-lg bg-background-50 p-6 mt-10">
            <h2 className="font-heading font-semibold text-lg text-foreground-900">
              {editing.id ? "Editar pregunta" : "Nueva pregunta"}
            </h2>
            {error && <div className="mt-3 rounded-md bg-accent-100 border border-accent-200 p-3 text-sm text-accent-800">{error}</div>}
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Pregunta</label>
                <input className={inputCls} value={editing.pregunta} onChange={(e) => setEditing({ ...editing, pregunta: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Respuesta</label>
                <textarea
                  className={inputCls}
                  rows={5}
                  value={editing.respuesta}
                  onChange={(e) => setEditing({ ...editing, respuesta: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-primary-500"
                  checked={editing.mostrar_en_inicio}
                  onChange={(e) => setEditing({ ...editing, mostrar_en_inicio: e.target.checked })}
                />
                Mostrar en el inicio
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="text-sm text-foreground-600 cursor-pointer">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
